import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getReconocimientosPorPadre } from '../../services/reconocimientoService';
import { formatearTotalReconocido, sumarTotalesReconocidos } from '../../utils/totalReconocido';
import FormularioIniciarTramiteReconocimiento from './FormularioIniciarTramiteReconocimiento';

const LABEL_SUBTIPO = { COMUN: 'Común', SERVICIO_DOMESTICO: 'Servicio Doméstico' };

const RESULTADO_META = {
    FAVORABLE: { label: 'Favorable', bg: '#dcfce7', color: '#166534' },
    DESFAVORABLE: { label: 'Desfavorable', bg: '#fee2e2', color: '#991b1b' },
    PENDIENTE: { label: 'Pendiente', bg: '#fef3c7', color: '#92400e' },
};

// La fecha viaja como 'YYYY-MM-DD'; se le fuerza la hora local para que no se corra un día
// por la conversión UTC del constructor de Date.
const formatearFechaResolucion = (fecha) => {
    if (!fecha) return '';
    const d = new Date(`${fecha}T00:00:00`);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-ES');
};

/**
 * Card "Reconocimientos de Servicios" para la ficha de un trámite padre (Jubilación o Plan de
 * Pago 27.705). El reconocimiento no se inicia suelto: se da de alta SOLO desde acá, embebido en
 * un modal, pre-vinculado a este padre y con el cliente pre-cargado (mismo CUIL). Un mismo padre
 * puede tener varios reconocimientos (ej. doméstico: se acredita un período, después se
 * consiguen más recibos y se hace otro).
 */
export default function CardReconocimientosServicios({ idTramitePadre, cliente }) {
    const navigate = useNavigate();
    const [reconocimientos, setReconocimientos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [modalAbierto, setModalAbierto] = useState(false);

    const cargar = useCallback(async () => {
        if (!idTramitePadre) return;
        setCargando(true);
        try {
            const data = await getReconocimientosPorPadre(idTramitePadre);
            setReconocimientos(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error al listar reconocimientos de servicios del trámite:', err);
            setReconocimientos([]);
        } finally {
            setCargando(false);
        }
    }, [idTramitePadre]);

    useEffect(() => { cargar(); }, [cargar]);

    const handleCreado = () => {
        setModalAbierto(false);
        cargar();
    };

    // Total acumulado de todos los reconocimientos del padre, normalizando el acarreo
    // (30 días → 1 mes, 12 meses → 1 año).
    const totalAcumulado = sumarTotalesReconocidos(reconocimientos);

    return (
        <div className="row g-4 mt-1">
            <div className="col-12">
                <div className="card shadow-lg border-0 rounded-4 p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="fw-bold text-primary mb-0">
                            <i className="bi bi-folder2-open me-2"></i>Reconocimientos de Servicios
                        </h4>
                        <button
                            className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill fw-semibold"
                            onClick={() => setModalAbierto(true)}
                        >
                            <span style={{ fontSize: '1.2rem' }}>+</span> Iniciar reconocimiento de servicios
                        </button>
                    </div>

                    {cargando ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status"></div>
                            <p className="mt-2 text-muted">Cargando reconocimientos...</p>
                        </div>
                    ) : reconocimientos.length === 0 ? (
                        <div className="alert alert-info border-0 rounded-3 text-center mb-0">
                            <p className="mb-0">No hay reconocimientos de servicios vinculados a este trámite.</p>
                        </div>
                    ) : (
                        <div className="row g-3">
                            {reconocimientos.map((r) => {
                                const resultado = r.resultadoResolucion ? RESULTADO_META[r.resultadoResolucion] : null;
                                const total = formatearTotalReconocido(r.aniosReconocidos, r.mesesReconocidos, r.diasReconocidos);
                                const colorBorde = resultado ? resultado.color : '#cbd5e1';
                                const fondoHover = resultado ? resultado.bg : '#eff6ff';
                                return (
                                    <div key={r.idTramite} className="col-md-6">
                                        <div
                                            className="rounded-4 p-3 h-100 shadow-sm"
                                            style={{ background: '#f8fafc', borderLeft: `5px solid ${colorBorde}`, cursor: 'pointer', transition: 'all 0.2s ease' }}
                                            onClick={() => navigate(`/reconocimiento/tramites/${r.idTramite}`)}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = fondoHover; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                                        >
                                            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                                <h6 className="fw-bold text-primary mb-0">
                                                    {LABEL_SUBTIPO[r.subtipo] || r.subtipo || 'Reconocimiento'}
                                                </h6>
                                                {resultado ? (
                                                    <span className="badge rounded-pill fw-semibold" style={{ background: resultado.bg, color: resultado.color }}>
                                                        {resultado.label}
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle rounded-pill">
                                                        {r.nombreEstadoActual || 'Ingreso del Trámite'}
                                                    </span>
                                                )}
                                            </div>
                                            {total ? (
                                                <div className="d-flex flex-column gap-1">
                                                    <small className="text-muted">Total reconocido</small>
                                                    <span className="fw-bold" style={{ color: resultado ? resultado.color : '#1f2937' }}>
                                                        {total}
                                                    </span>
                                                </div>
                                            ) : (
                                                <small className="text-muted fst-italic">
                                                    {resultado ? 'Sin total de servicios cargado' : 'Sin resolución cargada todavía'}
                                                </small>
                                            )}
                                            {r.fechaResolucion && (
                                                <small className="text-muted d-block mt-2">
                                                    <i className="bi bi-calendar-check me-1"></i>
                                                    Resolución del {formatearFechaResolucion(r.fechaResolucion)}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {totalAcumulado && (
                        <div
                            className="d-flex justify-content-between align-items-center rounded-4 p-3 mt-3"
                            style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}
                        >
                            <span className="fw-semibold text-secondary">
                                <i className="bi bi-calculator me-2"></i>
                                Total acumulado reconocido
                                {reconocimientos.length > 1 && (
                                    <small className="text-muted fw-normal ms-2">({reconocimientos.length} reconocimientos)</small>
                                )}
                            </span>
                            <span className="fw-bold fs-5 text-primary">
                                {formatearTotalReconocido(totalAcumulado.anios, totalAcumulado.meses, totalAcumulado.dias)}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal: iniciar reconocimiento embebido, pre-vinculado a este padre */}
            <Dialog open={modalAbierto} onClose={() => setModalAbierto(false)} maxWidth="md" fullWidth>
                <IconButton
                    onClick={() => setModalAbierto(false)}
                    sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent sx={{ p: 3 }}>
                    <FormularioIniciarTramiteReconocimiento
                        idTramitePadre={idTramitePadre}
                        cuilFijo={cliente?.cuil}
                        onSuccess={handleCreado}
                        onCancel={() => setModalAbierto(false)}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}
